import { WebViewerInstance } from '@pdftron/webviewer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { MutationParameters, PayloadError } from 'relay-runtime';

import { useDrawingSetAnnotation } from '../../../../../service/drawings/src/ui/hooks/use-loader-drawing-set-annotation';
import { drawingSetAnnotationModel } from '../../../../../service/drawings/src/ui/model/drawing-set-annotation-model';
import { DrawingSheetOrDocumentIdDataType } from '../../../../../service/drawings/src/ui/types/types';
import { DEBOUNCE_DELAY } from '../../../../../shared/const';
import { DocumentViewerType } from '../../../../global-type';

let bufferUpdateValue = 0;

const SKIP_ANNOTATION_TAGS = new Set(['popup', 'reply']);
const SKIP_SUBJECTS = ['SMX_LINK_VISUAL'];

const hasMinPointsInGesture = (el: string, minPairs: number) => {
    const gestures = el.match(/<\s*gesture[^>]*>([\s\S]*?)<\/\s*gesture\s*>/gi) || [];
    const totalPairs = gestures.reduce((acc, gesture) => {
        const content = gesture.replace(/<\s*\/?gesture[^>]*>/gi, '');
        const nums = content.match(/-?\d+(?:\.\d+)?/g) || [];
        return acc + Math.floor(nums.length / 2);
    }, 0);
    return totalPairs >= minPairs;
};

const hasMinVertices = (el: string, minPairs: number) => {
    const verticesMatch = el.match(/<\s*vertices[^>]*>([\s\S]*?)<\/\s*vertices\s*>/i);
    if (!verticesMatch) return false;
    const nums = verticesMatch[1].match(/-?\d+(?:\.\d+)?/g) || [];
    return Math.floor(nums.length / 2) >= minPairs;
};

// Interactive form field values live in a <fields> element that is a SIBLING of
// <annots>. A <field> nested inside <annots> is discarded without any error, so
// the two lanes are kept apart on the way in and on the way out.
const FORM_FIELDS_TYPE = 'FormFields';

const isFormFieldsPayload = (xfdf: string) => /^\s*<\s*fields\b/i.test(xfdf);

/** Buffer slot for the form state, kept apart from the per-annotation entries. */
const FORM_FIELDS_BUFFER_KEY = 'smx-form-fields';

/** Upper bound on waiting for a single mutation before the flush moves on. */
const COMMIT_TIMEOUT = 15000;

/**
 * Whether a buffered write actually landed.
 *
 * The flush has to distinguish "stopped pending" from "succeeded": a consumer
 * cuts a new flattened PDF version once the flush reports done, and doing that
 * after a failed write flattens the state the server still holds - the one from
 * before the edit - while telling the user their markup was saved.
 */
export type CommitOutcome = { ok: boolean; reason?: string };

/** The <fields> element is stored whole: hierarchy is expressed by nesting it. */
const extractFieldsBlock = (xfdf: string): string => {
    const match = xfdf.match(/<fields\b[^>]*>([\s\S]*?)<\/fields>/i);

    return match ? match[1].trim() : '';
};

const wrapXfdf = (fragments: string[], fieldFragments: string[] = []) => {
    const fields = fieldFragments.length ? `<fields>${fieldFragments.join('')}</fields>` : '';
    const annots = fragments.length ? `<annots>${fragments.join('\n')}</annots>` : '';

    return `<?xml version="1.0" encoding="UTF-8" ?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">${fields}${annots}</xfdf>`;
};

const stripApref = (fragment: string) => {
    const updated = fragment
        // apref attribute (quoted)
        .replace(/\s+apref\s*=\s*(['"])[\s\S]*?\1/gi, '')
        // apref attribute (unquoted)
        .replace(/\s+apref\s*=\s*[^\s/>]+/gi, '')
        // apref element (paired)
        .replace(/<\s*apref\b[^>]*>[\s\S]*?<\/\s*apref\s*>/gi, '')
        // apref element (self-closing)
        .replace(/<\s*apref\b[^>]*\/\s*>/gi, '')
        // apref-replace element (paired)
        .replace(/<\s*apref-replace\b[^>]*>[\s\S]*?<\/\s*apref-replace\s*>/gi, '')
        // apref-replace element (self-closing)
        .replace(/<\s*apref-replace\b[^>]*\/\s*>/gi, '');

    return updated;
};

const extractAnnotationFragments = (xfdf: string): string[] => {
    const trimmed = xfdf.trim();
    if (!trimmed) return [];

    const annotsMatch = trimmed.match(/<annots[^>]*>([\s\S]*?)<\/annots>/i);
    if (!annotsMatch && /<xfdf\b/i.test(trimmed)) {
        return [];
    }

    const content = annotsMatch ? annotsMatch[1] : trimmed;
    const elements = content.match(/<([A-Za-z][\w:-]*)\b[^>]*(?:\/>|>[\s\S]*?<\/\1>)/g) || [];

    return elements
        .map((el) => el.trim())
        .filter((el) => !/^<\s*\/?\s*(xfdf|annots)\b/i.test(el))
        .filter((el) => {
            const tag = el.match(/^<\s*([A-Za-z][\w:-]*)\b/i)?.[1]?.toLowerCase();
            if (tag && SKIP_ANNOTATION_TAGS.has(tag)) return false;
            return true;
        })
        .filter((el) => !SKIP_SUBJECTS.some((subject) => el.includes(`subject="${subject}"`)))
        .filter((el) => {
            const tag = el.match(/^<\s*([A-Za-z][\w:-]*)\b/i)?.[1]?.toLowerCase();
            if (!tag) return false;
            if (tag === 'ink') {
                return hasMinPointsInGesture(el, 2);
            }
            if (tag === 'polygon') {
                return hasMinVertices(el, 3);
            }
            if (tag === 'polyline') {
                return hasMinVertices(el, 2);
            }
            return true;
        })
        .map((el) => {
            const cleaned = stripApref(el);
            return cleaned;
        })
        .filter((el) => /\bname\s*=\s*['"][^'"]+['"]/i.test(el) || /\bNM\s*=\s*['"][^'"]+['"]/i.test(el));
};

export const useAnnotationViewerAppryze = (
    webViewer: WebViewerInstance | null,
    accessSaving: boolean,
    sheetData?: DrawingSheetOrDocumentIdDataType,
    bufferAnnotation?: boolean,
    bufferAnnotationListRef?: React.RefObject<any>,
    onUpdateAnnotations?: (flag: boolean) => void,
) => {
    const [currentDrawingSheetOrDocumentId, setCurrentDrawingSheetOrDocumentId] = useState<string>();
    const [updateValue, setUpdateValue] = useState<number>(bufferUpdateValue);
    const [openedFileName, setOpenedFileName] = useState<string>('');
    const [internalAnnotationList, setInternalAnnotationList] = useState<string>('');
    const [isDocumentLoaded, setIsDocumentLoaded] = useState<boolean>(false);
    const pendingImportRef = useRef<string | null>(null);

    const annotationList = useDrawingSetAnnotation(updateValue, currentDrawingSheetOrDocumentId);
    const timerRef = useRef<NodeJS.Timeout>(null);
    const formFieldsRowIdRef = useRef<string | null>(null);
    const formFieldsTimerRef = useRef<NodeJS.Timeout>(null);
    // const internalWebViewer = useRef<WebViewerInstance>(null);

    const model = drawingSetAnnotationModel;

    const [commitAnnotationCreate] = useMutation(model.mutation!.create!);
    const [commitAnnotationUpdate] = useMutation(model.mutation!.update!);
    const [commitAnnotationDelete] = useMutation(model.mutation!.delete!);

    useEffect(() => {
        const { filename } = (webViewer?.Core.documentViewer.getDocument() as unknown as DocumentViewerType) || {};
        setOpenedFileName(filename);
    }, [webViewer]);

    const importAnnotationsSafely = useCallback(
        async (xfdf: string) => {
            if (!webViewer) {
                pendingImportRef.current = xfdf;
                return;
            }

            const { annotationManager, documentViewer } = webViewer.Core;
            if (!documentViewer?.getDocument()) {
                pendingImportRef.current = xfdf;
                return;
            }

            try {
                await annotationManager.importAnnotations(xfdf);
            } catch (err) {
                // Keep silent to avoid console noise
            }
        },
        [webViewer],
    );

    useEffect(() => {
        if (!webViewer) return;

        const { documentViewer } = webViewer.Core;
        const onDocumentLoaded = () => {
            setIsDocumentLoaded(true);
            if (pendingImportRef.current) {
                const xfdf = pendingImportRef.current;
                pendingImportRef.current = null;
                void importAnnotationsSafely(xfdf);
            }
        };

        documentViewer.addEventListener('documentLoaded', onDocumentLoaded);

        if (documentViewer.getDocument()) {
            setIsDocumentLoaded(true);
        }

        return () => {
            documentViewer.removeEventListener('documentLoaded', onDocumentLoaded);
        };
    }, [webViewer, importAnnotationsSafely]);

    useEffect(() => {
        bufferUpdateValue = updateValue + 1;
        setUpdateValue(bufferUpdateValue);
    }, [sheetData?.sheetId, sheetData?.documentId, openedFileName]);

    useEffect(() => {
        if (!internalAnnotationList) {
            return;
        }

        // Routed by payload shape rather than the row's `type`: the list fragment
        // masks `type`, and the shape is self-describing anyway.
        const formFieldRows = annotationList.filter((annotation) => isFormFieldsPayload(annotation.node.xfdf ?? ''));
        const annotationRows = annotationList.filter((annotation) => !isFormFieldsPayload(annotation.node.xfdf ?? ''));

        const extractedByEdge = annotationRows.map((annotation) =>
            extractAnnotationFragments(annotation.node.xfdf ?? ''),
        );
        const listTextAnnotation = extractedByEdge.flat().filter((xfdf): xfdf is string => Boolean(xfdf));

        const listFieldFragments = formFieldRows
            .map((annotation) => extractFieldsBlock(annotation.node.xfdf ?? ''))
            .filter(Boolean);

        if (listTextAnnotation.length === 0 && listFieldFragments.length === 0) {
            return;
        }

        // Avoid commas between annotations; XFDF expects adjacent elements inside <annots>.
        const textXfdf = wrapXfdf(listTextAnnotation, listFieldFragments);

        if (!isDocumentLoaded) {
            pendingImportRef.current = textXfdf;
            return;
        }

        void importAnnotationsSafely(textXfdf);
    }, [internalAnnotationList, annotationList, isDocumentLoaded, importAnnotationsSafely]);

    useEffect(() => {
        setInternalAnnotationList(annotationList.map((annotation) => annotation.cursor).join());
    }, [annotationList]);

    // One row holds the whole form state, so a save updates it rather than
    // appending another. Discovered from the loaded list by shape.
    useEffect(() => {
        const existing = annotationList.find((annotation) => isFormFieldsPayload(annotation.node.xfdf ?? ''));
        formFieldsRowIdRef.current = (existing?.node.id as string | undefined) ?? formFieldsRowIdRef.current ?? null;
    }, [annotationList]);

    /**
     * Relay commit functions are fire-and-forget, so awaiting one tells you
     * nothing. The buffered flush needs to know when a write really landed:
     * without this the caller cuts the new PDF version before the annotations
     * exist server side, which is what the modal's arbitrary 2s wait was hiding.
     * Bounded, so one stuck mutation cannot wedge the save.
     */
    const commitAndWait = (commit: (config: any) => unknown, params: MutationParameters): Promise<CommitOutcome> =>
        new Promise((resolve) => {
            const config = params as any;
            let settled = false;
            const finish = (outcome: CommitOutcome) => {
                if (settled) return;
                settled = true;
                resolve(outcome);
            };

            // A stuck mutation must not wedge the save, but timing out is a
            // failure rather than a completion.
            const timeout = setTimeout(() => finish({ ok: false, reason: 'timed out' }), COMMIT_TIMEOUT);

            commit({
                ...config,
                onCompleted: (response: any, errors: any) => {
                    clearTimeout(timeout);
                    config.onCompleted?.(response, errors);
                    // Relay calls onCompleted even when the payload carries GraphQL
                    // errors, so "completed" is not "succeeded".
                    finish(
                        errors?.length
                            ? { ok: false, reason: errors[0]?.message ?? 'rejected by the server' }
                            : { ok: true },
                    );
                },
                onError: (error: any) => {
                    clearTimeout(timeout);
                    config.onError?.(error);
                    finish({ ok: false, reason: error?.message ?? 'request failed' });
                },
            });
        });

    const commitDeleteAnnotationModel = (id: string): MutationParameters => {
        return {
            variables: {
                id,
                connections: [],
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    console.log('Annotation delete successful', response);
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as unknown as MutationParameters;
    };

    const commitAnnotationModel = (data: any, id?: string): MutationParameters => {
        const newInput = {
            ...data,
        };

        return {
            variables: {
                id,
                input: newInput,
                connections: [],
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    console.log('Annotation save successful', response);
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as unknown as MutationParameters;
    };

    const setupAnnotationInterception = (instance: WebViewerInstance) => {
        const { Core } = instance;
        const annotManager = Core.annotationManager;

        const onAnnotationChanged = async (
            // @ts-expect-error ignore
            annotations: Core.Annotations.Annotation[],
            action: 'add' | 'modify' | 'delete',
            info: { imported?: boolean } = {},
        ) => {
            if (
                !['add', 'modify', 'delete'].includes(action) ||
                info?.imported ||
                (annotations[0].Subject === 'Annotation' && !accessSaving)
            ) {
                return;
            }

            onUpdateAnnotations?.(true);

            for (const annot of annotations) {
                try {
                    const annotationXfdf =
                        (
                            await annotManager.exportAnnotations({
                                annotationList: [annot],
                                links: false,
                                widgets: false,
                            })
                        ).match(/<annots>(.*?)<\/annots>/s)?.[1] || '';

                    const allElements = annotationXfdf.match(/<([A-Za-z][\w:-]*)\b[^>]*(?:\/>|>[\s\S]*?<\/\1>)/g) || [];

                    const annotIdMatch = (el: string) =>
                        new RegExp(`\\b(?:name|NM)\\s*=\\s*['"]${annot.Id}['"]`, 'i').test(el);

                    // Array.find returns the matched element (a string XFDF fragment) or undefined.
                    // The previous `const [bodyAnnotation] = ...` destructured the string and
                    // extracted only its first character ("<"), which is why every saved
                    // annotation's xfdf was just "<". Keep this as a plain assignment.
                    const bodyAnnotation =
                        allElements.find((el) => annotIdMatch(el)) ||
                        allElements.find((el) => {
                            const tag = el.match(/^<\s*([A-Za-z][\w:-]*)\b/i)?.[1]?.toLowerCase();
                            return tag ? !SKIP_ANNOTATION_TAGS.has(tag) : false;
                        }) ||
                        '';

                    if (!bodyAnnotation) {
                        continue;
                    }

                    const sanitizedBodyAnnotation = stripApref(bodyAnnotation);

                    const payload = {
                        id: annot.Id,
                        type: annot.Subject || annot.SubjectName || annot.getType?.() || 'Annotation',
                        drawingSetId: sheetData?.drawingSetId || sheetData?.documentId,
                        drawingSheetId: sheetData?.sheetId || sheetData?.documentId,
                        xfdf: sanitizedBodyAnnotation,
                    };

                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }

                    timerRef.current = setTimeout(() => {
                        const allowAction = bufferAnnotation && bufferAnnotationListRef?.current;
                        const bufferMap = bufferAnnotationListRef?.current || {};
                        bufferMap[annot.Id] = bufferMap[annot.Id] || [];
                        switch (action) {
                            case 'add':
                                if (allowAction) {
                                    bufferMap[annot.Id].push({
                                        fn: () =>
                                            commitAndWait(
                                                commitAnnotationCreate,
                                                commitAnnotationModel(payload, undefined),
                                            ),
                                        action,
                                    });
                                } else {
                                    commitAnnotationCreate(commitAnnotationModel(payload, undefined));
                                }
                                break;
                            case 'modify':
                                delete payload.id;
                                if (allowAction) {
                                    bufferMap[annot.Id].push({
                                        fn: () =>
                                            commitAndWait(
                                                commitAnnotationUpdate,
                                                commitAnnotationModel(payload, annot.Id),
                                            ),
                                        action,
                                    });
                                } else {
                                    commitAnnotationUpdate(commitAnnotationModel(payload, annot.Id));
                                }
                                break;
                            case 'delete':
                                if (allowAction) {
                                    bufferMap[annot.Id].push({
                                        fn: () =>
                                            commitAndWait(
                                                commitAnnotationDelete,
                                                commitDeleteAnnotationModel(annot.Id),
                                            ),
                                        action,
                                    });
                                } else {
                                    commitAnnotationDelete(commitDeleteAnnotationModel(annot.Id));
                                }
                                break;
                            default:
                        }
                    }, DEBOUNCE_DELAY);
                } catch {
                    // console.error('Failed to export/save annotation', e);
                }
            }
        };

        /**
         * Export and persist the whole form state.
         *
         * exportAnnotations returns every field, not just the edited ones, so one
         * row holds a complete snapshot and repeated saves are idempotent. The
         * <fields> element is stored exactly as WebViewer wrote it — hierarchy is
         * expressed by nesting, and rebuilding it from parts is what silently
         * breaks the merge.
         */
        const commitFormFields = async (): Promise<CommitOutcome> => {
            try {
                const exported = await annotManager.exportAnnotations({
                    fields: true,
                    widgets: false,
                    links: false,
                });

                const fieldsBlock = extractFieldsBlock(exported);
                if (!fieldsBlock) {
                    // Nothing to store is not a failure.
                    return { ok: true };
                }

                const payload = {
                    drawingSetId: sheetData?.drawingSetId || sheetData?.documentId,
                    drawingSheetId: sheetData?.sheetId || sheetData?.documentId,
                    type: FORM_FIELDS_TYPE,
                    xfdf: `<fields>${fieldsBlock}</fields>`,
                };

                const existingId = formFieldsRowIdRef.current;
                if (existingId) {
                    return await commitAndWait(commitAnnotationUpdate, commitAnnotationModel(payload, existingId));
                }

                // Claim the id before the round trip so a second save in the same
                // session updates this row instead of adding another one.
                const id = globalThis.crypto?.randomUUID?.() ?? `form-fields-${Date.now()}`;
                formFieldsRowIdRef.current = id;

                const outcome = await commitAndWait(
                    commitAnnotationCreate,
                    commitAnnotationModel({ ...payload, id }, undefined),
                );

                if (!outcome.ok) {
                    // The row was never created, so releasing the claim keeps a
                    // retry on the create path; otherwise every later save would
                    // update an id the server has never seen.
                    formFieldsRowIdRef.current = null;
                }

                return outcome;
            } catch (error: any) {
                // Still must not break the viewer - but the caller has to be told,
                // or it will cut a PDF version from form state that never landed.
                return { ok: false, reason: error?.message ?? 'could not export the form state' };
            }
        };

        // Typing into a form field raises fieldChanged, never annotationChanged,
        // which is why the Save button never noticed these edits.
        const onFieldChanged = () => {
            if (!accessSaving) {
                return;
            }

            onUpdateAnnotations?.(true);

            // Deliberately its own timer: the shared one above is cleared by every
            // annotation change, so fast successive edits would drop each other.
            if (formFieldsTimerRef.current) {
                clearTimeout(formFieldsTimerRef.current);
            }

            formFieldsTimerRef.current = setTimeout(() => {
                if (bufferAnnotation && bufferAnnotationListRef?.current) {
                    // Replaced rather than appended: each export is a full snapshot,
                    // so only the last one is worth sending.
                    bufferAnnotationListRef.current[FORM_FIELDS_BUFFER_KEY] = [
                        { fn: () => commitFormFields(), action: 'modify' },
                    ];

                    return;
                }

                void commitFormFields();
            }, DEBOUNCE_DELAY);
        };

        annotManager.addEventListener('annotationChanged', onAnnotationChanged);
        annotManager.addEventListener('fieldChanged', onFieldChanged);

        return () => {
            annotManager.removeEventListener('annotationChanged', onAnnotationChanged);
            annotManager.removeEventListener('fieldChanged', onFieldChanged);
            if (formFieldsTimerRef.current) {
                clearTimeout(formFieldsTimerRef.current);
            }
        };
    };

    return {
        setCurrentDrawingSheetOrDocumentId,
        setupAnnotationInterception,
    };
};
